<template>
  <div>
    <base-header class="pb-6" />
    <b-container fluid class="mt--6 max-width">
      <b-card no-body class="elegant-card">
        <!-- Card Header -->
        <card-header title="Upload Devices" class="elegant-header">
        </card-header>

        <validation-observer v-slot="{ handleSubmit }" ref="formValidator">
          <b-form role="form" @submit.prevent="handleSubmit(onSubmit)" class="elegant-form">
            <b-card-body class="pt-4 pb-1">
              <!-- Manufacturing Type Input & LoRa Toggle -->
              <b-row class="mb-4">
                <b-col md="6">
                  <base-input
                    label="Manufacture Type"
                    name="manufacture_type"
                    v-model="form.manufacture_type"
                    :rules="{ required: true }"
                    class="elegant-input"
                    placeholder="Enter device manufacture type"
                  />
                </b-col>
                <b-col md="6" class="d-flex align-items-center pt-4">
                  <div>
                    <b-form-checkbox
                      v-model="addToLora"
                      :disabled="!loraAvailable || isLoading"
                      switch
                    >
                      Add all devices to LoRa / TTN
                    </b-form-checkbox>
                    <small v-if="!loraAvailable" class="text-muted">
                      <i class="fas fa-info-circle mr-1"></i>
                      TTN integration is not configured or OK for this entity.
                    </small>
                  </div>
                </b-col>
              </b-row>

              <!-- CSV File Upload -->
              <b-row>
                <b-col md="8">
                  <div class="file-upload-container">
                    <label class="form-control-label d-block mb-2">CSV File</label>
                    <div class="custom-file-upload" :class="{ 'has-file': file && !hasValidationErrors, 'has-error': file && hasValidationErrors }">
                      <input
                        type="file"
                        @change="onFileUpload"
                        accept=".csv"
                        id="csv-upload"
                        class="file-input"
                      />
                      <label for="csv-upload" class="file-label">
                        <i class="fas fa-cloud-upload-alt mr-2"></i>
                        <span v-if="!file">Choose CSV file</span>
                        <span v-else class="file-name">{{ file.name }}</span>
                      </label>
                      <div class="file-hint mt-2">
                        <small class="text-muted">
                          <i class="fas fa-info-circle mr-1"></i>
                          CSV should include a Device ID column (12 characters, e.g. 38b8ebe10350).
                          <span v-if="addToLora">
                            Include DevEUI, JoinEUI, and AppKey columns for LoRa.
                          </span>
                        </small>
                      </div>
                    </div>
                  </div>
                </b-col>
                <b-col md="4" class="d-flex align-items-end">
                  <a href="#" class="download-template" @click.prevent="downloadTemplate">
                    <i class="fas fa-download mr-1"></i> Download CSV template
                  </a>
                </b-col>
              </b-row>

              <!-- Validation errors -->
              <b-row v-if="hasValidationErrors" class="mt-4">
                <b-col>
                  <b-alert show variant="danger" class="mb-0">
                    <h6 class="alert-heading mb-2">
                      <i class="fas fa-exclamation-triangle mr-1"></i>
                      Fix the CSV and upload it again
                    </h6>

                    <div v-if="fileError" class="mb-2">
                      <strong>{{ fileError }}</strong>
                    </div>

                    <div v-if="validation.invalid.length" class="mb-2">
                      <strong>{{ validation.invalid.length }} invalid row(s)</strong>
                      <ul class="mb-0 mt-1">
                        <li v-for="item in validation.invalid.slice(0, 10)" :key="'invalid-' + item.row">
                          Row {{ item.row }}: <code>{{ item.id }}</code> — {{ item.reason }}
                        </li>
                        <li v-if="validation.invalid.length > 10">
                          + {{ validation.invalid.length - 10 }} more
                        </li>
                      </ul>
                    </div>

                    <div v-if="validation.duplicates.length">
                      <strong>{{ validation.duplicates.length }} duplicated Device ID(s)</strong>
                      <ul class="mb-0 mt-1">
                        <li v-for="dup in validation.duplicates.slice(0, 10)" :key="'dup-' + dup.id">
                          <code>{{ dup.id }}</code> appears in rows {{ dup.rows.join(', ') }}
                        </li>
                        <li v-if="validation.duplicates.length > 10">
                          + {{ validation.duplicates.length - 10 }} more
                        </li>
                      </ul>
                    </div>
                  </b-alert>
                </b-col>
              </b-row>

              <!-- Preview Table -->
              <b-row v-if="form.devices.length > 0" class="mt-5">
                <b-col>
                  <h6 class="heading-small text-muted mb-3">Preview ({{ form.devices.length }} devices)</h6>
                  <div class="table-responsive">
                    <table class="table table-flush elegant-table">
                      <thead class="thead-light">
                        <tr>
                          <th scope="col">Device ID</th>
                          <th v-if="addToLora" scope="col">DevEUI</th>
                          <th v-if="addToLora" scope="col">JoinEUI</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(device, index) in form.devices.slice(0, 5)" :key="index">
                          <td>{{ device.name }}</td>
                          <td v-if="addToLora">{{ device.dev_eui }}</td>
                          <td v-if="addToLora">{{ device.join_eui }}</td>
                        </tr>
                        <tr v-if="form.devices.length > 5">
                          <td :colspan="addToLora ? 3 : 1" class="text-center text-muted">
                            + {{ form.devices.length - 5 }} more devices
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </b-col>
              </b-row>
            </b-card-body>

            <!-- Form Actions -->
            <b-card-footer class="d-flex justify-content-between align-items-center elegant-footer">
              <div class="text-muted" v-if="form.devices.length > 0">
                Ready to import {{ form.devices.length }} devices
              </div>
              <div v-else></div>

              <div>
                <base-button
                  type="secondary"
                  size="sm"
                  @click="onCancel"
                  class="mr-2"
                  outline
                >
                  Cancel
                </base-button>
                <base-button
                  type="primary"
                  size="sm"
                  native-type="submit"
                  :loading="isLoading"
                  :disabled="!form.manufacture_type || form.devices.length === 0 || hasValidationErrors"
                >
                  <i class="fas fa-plus-circle mr-1"></i> Add Devices
                </base-button>
              </div>
            </b-card-footer>
          </b-form>
        </validation-observer>
      </b-card>
    </b-container>

    <!-- Bulk Result Modal -->
    <b-modal v-model="showResultModal" title="Bulk Import Results" hide-footer size="lg">
      <b-alert :variant="failedDevices.length ? 'warning' : 'success'" show>
        <h5 class="alert-heading mb-1">
          {{ successCount }} provisioned successfully, {{ failedDevices.length }} failed.
        </h5>
        <p class="mb-0" v-if="!failedDevices.length">
          All devices were created and added to TTN successfully.
        </p>
      </b-alert>

      <div v-if="failedDevices.length">
        <h6 class="mt-4 mb-2">Failed Devices</h6>
        <b-table small striped :items="failedDevices" :fields="['name', 'error']"></b-table>
      </div>

      <div class="text-right mt-4">
        <base-button type="primary" @click="closeResultModal">Close</base-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Routes } from "@/router";
import { Action } from "@/store/actions";
import Papa from "papaparse";

// 12 hex characters, e.g. 38b8ebe10350
const DEVICE_ID_REGEX = /^[0-9a-f]{12}$/i;
const DEFAULT_JOIN_EUI = "38b8ebe000000001";
const DEFAULT_APP_KEY = "00112233445566778899aabbccddeeff";

export default {
  name: "DevicesNew",
  data() {
    return {
      form: {
        manufacture_type: '',
        devices: []
      },
      file: null,
      isLoading: false,
      addToLora: false,
      fileError: '',
      validation: { invalid: [], duplicates: [] },
      showResultModal: false,
      successCount: 0,
      failedDevices: []
    };
  },
  computed: {
    activeEntity() {
      return this.$store.getters.activeEntity;
    },
    loraIntegration() {
      return this.$store.getters.ttnIntegration(this.activeEntity);
    },
    loraAvailable() {
      return !!(this.loraIntegration && this.loraIntegration.status === "ok");
    },
    hasValidationErrors() {
      return !!this.fileError || this.validation.invalid.length > 0 || this.validation.duplicates.length > 0;
    }
  },
  watch: {
    addToLora() {
      // Re-validate if the user toggles LoRa after loading a file
      if (this.file) {
        const mockEvent = { target: { files: [this.file], value: '' } };
        this.onFileUpload(mockEvent);
      }
    }
  },
  created() {
    if (this.activeEntity) {
      this.$store.dispatch(Action.ttn.load, this.activeEntity).catch(() => {});
    }
  },
  methods: {
    onCancel() {
      this.$router.push({ name: Routes.deviceList });
    },

    deriveDevEui(deviceId) {
      if (!DEVICE_ID_REGEX.test(deviceId)) return "";
      return deviceId.slice(0, 8) + "0000" + deviceId.slice(-4);
    },

    onFileUpload(event) {
      const file = event.target.files[0];
      this.file = file || null;
      this.form.devices = [];
      this.fileError = '';
      this.validation = { invalid: [], duplicates: [] };

      if (!file) return;

      Papa.parse(file, {
        header: true,
        transformHeader: (h) => {
          const clean = h.replace(/^\uFEFF/, '').trim();
          const key = clean.toLowerCase().replace(/[^a-z0-9]/g, '');
          if (key === 'deviceid') return 'Device ID';
          if (key === 'description') return 'Description';
          if (key === 'versionid') return 'Version ID';
          if (key === 'deveui') return 'DevEUI';
          if (key === 'joineui') return 'JoinEUI';
          if (key === 'appkey') return 'AppKey';
          return clean;
        },
        complete: (results) => {
          this.processRows(results.data, (results.meta && results.meta.fields) || []);
          event.target.value = '';
        },
        error: (error) => {
          console.error("Error parsing CSV:", error);
          this.$notify({ title: 'Error', message: 'Failed to parse CSV.', type: 'error' });
        }
      });
    },

    processRows(rows, fields = []) {
      if (!fields.includes('Device ID')) {
        this.fileError = 'No "Device ID" column found.';
        this.notifyErrors();
        return;
      }

      const invalid = [];
      const seen = new Map();

      rows.forEach((row, index) => {
        const rawId = (row['Device ID'] || '').trim();
        if (!rawId) return;

        const rowNumber = index + 2;
        const id = rawId.toLowerCase();

        // 1. Base validation
        if (!DEVICE_ID_REGEX.test(id)) {
          invalid.push({ row: rowNumber, id, reason: "Device ID must be 12 hex characters" });
          return;
        }

        // 2. LoRa validation (if checked)
        let dev_eui, join_eui, app_key;
        if (this.addToLora) {
          const csvDevEui = (row['DevEUI'] || '').trim();
          const csvJoinEui = (row['JoinEUI'] || '').trim();
          const csvAppKey = (row['AppKey'] || '').trim();

          dev_eui = csvDevEui || this.deriveDevEui(id);
          join_eui = csvJoinEui || DEFAULT_JOIN_EUI;
          app_key = csvAppKey || DEFAULT_APP_KEY;

          if (!/^[0-9a-f]{16}$/i.test(dev_eui)) {
            invalid.push({ row: rowNumber, id, reason: `Invalid DevEUI: ${dev_eui}` });
            return;
          }
          if (!/^[0-9a-f]{16}$/i.test(join_eui)) {
            invalid.push({ row: rowNumber, id, reason: `Invalid JoinEUI: ${join_eui}` });
            return;
          }
          if (!/^[0-9a-f]{32}$/i.test(app_key)) {
            invalid.push({ row: rowNumber, id, reason: `Invalid AppKey (must be 32 hex)` });
            return;
          }
        }

        if (!seen.has(id)) seen.set(id, { rows: [], source: row, lora: {} });
        const entry = seen.get(id);
        entry.rows.push(rowNumber);
        
        if (this.addToLora) {
          entry.lora = { dev_eui, join_eui, app_key };
        }
      });

      const duplicates = [];
      const devices = [];

      seen.forEach((entry, id) => {
        if (entry.rows.length > 1) {
          duplicates.push({ id, rows: entry.rows });
        }
        const dev = {
          name: id,
          description: entry.source['Description'] || '',
          manufacturing_type: this.form.manufacture_type || 'CC1',
          version_id: entry.source['Version ID'] || ''
        };
        
        if (this.addToLora) {
          Object.assign(dev, {
            transport: 'lora',
            provider: 'ttn',
            provision_status: 'pending',
            frequency_plan_id: 'EU_863_870_TTN',
            lorawan_version: 'MAC_V1_0_2',
            lorawan_phy_version: 'PHY_V1_0_2_REV_B',
            supports_class_c: true,
            dev_eui: entry.lora.dev_eui,
            join_eui: entry.lora.join_eui,
            app_key: entry.lora.app_key
          });
        }

        devices.push(dev);
      });

      this.validation = { invalid, duplicates };

      if (!seen.size && !invalid.length) {
        this.fileError = 'No Device IDs found in this file.';
      }

      this.form.devices = this.hasValidationErrors ? [] : devices;
      if (this.hasValidationErrors) this.notifyErrors();
    },

    notifyErrors() {
      const parts = [];
      if (this.fileError) parts.push(this.fileError);
      if (this.validation.invalid.length) parts.push(`${this.validation.invalid.length} invalid rows`);
      if (this.validation.duplicates.length) parts.push(`${this.validation.duplicates.length} duplicates`);
      this.$notify({ title: 'Invalid CSV', message: parts.join(' · '), type: 'error' });
    },

    async onSubmit() {
      if (this.hasValidationErrors) return;
      if (!this.form.manufacture_type || this.form.devices.length === 0) {
        this.$notify({ title: 'Validation Error', message: 'Check the file and manufacture type.', type: 'warning' });
        return;
      }

      this.isLoading = true;
      this.successCount = 0;
      this.failedDevices = [];

      const devicesToAdd = this.form.devices.map(device => ({
        ...device,
        manufacturing_type: this.form.manufacture_type
      }));

      try {
        if (!this.addToLora) {
          // Normal Wi-Fi bulk upload (parallel)
          const promises = devicesToAdd.map(device => this.$store.dispatch(Action.device.add, device));
          await Promise.all(promises);
          this.successCount = devicesToAdd.length;
          this.$notify({ title: 'Success', message: `${this.successCount} devices added.`, type: 'success' });
          this.finishUpload();
        } else {
          // LoRa bulk upload (sequential to avoid hammering TTN)
          for (const device of devicesToAdd) {
            try {
              // 1. Create in IES
              await this.$store.dispatch(Action.device.add, device);
              // 2. Provision in TTN
              await this.$store.dispatch(Action.ttn.deviceProvision, {
                entity: this.activeEntity,
                name: device.name
              });
              this.successCount++;
            } catch (err) {
              const msg = (err && (err.message || err.provision_detail)) || "Provisioning failed";
              this.failedDevices.push({ name: device.name, error: msg });
            }
          }
          this.showResultModal = true;
        }
      } catch (error) {
        console.error("Bulk upload error:", error);
        this.$notify({ title: 'Error', message: 'Failed to upload devices.', type: 'error' });
      } finally {
        this.isLoading = false;
      }
    },

    finishUpload() {
      this.form.manufacture_type = '';
      this.form.devices = [];
      this.file = null;
      this.validation = { invalid: [], duplicates: [] };
      setTimeout(() => this.$router.push({ name: Routes.deviceList }), 1500);
    },

    closeResultModal() {
      this.showResultModal = false;
      this.finishUpload();
    },

    downloadTemplate() {
      let csvContent = "data:text/csv;charset=utf-8,Device ID,Description,Version ID";
      if (this.addToLora) {
        csvContent += ",DevEUI,JoinEUI,AppKey";
      }
      csvContent += "\n38b8ebe10021,Sample Device 1,1.0";
      if (this.addToLora) {
        csvContent += ",38b8ebe100000021,38b8ebe000000001,00112233445566778899aabbccddeeff";
      }

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "device_upload_template.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};
</script>

<style scoped>
.max-width { max-width: 1200px; margin: 0 auto; }
.elegant-card { border: none; border-radius: 10px; box-shadow: 0 0 2rem 0 rgba(136, 152, 170, 0.15); }
.elegant-header { background: linear-gradient(87deg, #f6f6f6 0, #edebf2 100%); color: white; border-radius: 10px 10px 0 0; }
.elegant-header .subtitle { color: rgba(247, 22, 22, 0.8); font-weight: 600; }
.elegant-form { padding: 0; }
.elegant-input >>> .form-control { border: 1px solid #dee2e6; border-radius: 6px; padding: 10px 15px; transition: all 0.2s ease; }
.elegant-input >>> .form-control:focus { border-color: #5e72e4; box-shadow: 0 3px 9px rgba(50, 50, 93, 0.06), 0 2px 5px rgba(0, 0, 0, 0.08); }
.file-upload-container { margin-bottom: 1.5rem; }
.custom-file-upload { position: relative; }
.file-input { position: absolute; left: -9999px; }
.file-label { display: block; padding: 12px 20px; background-color: #f7fafc; border: 2px dashed #dee2e6; border-radius: 6px; text-align: center; cursor: pointer; transition: all 0.3s ease; }
.file-label:hover { border-color: #f7fafc; background-color: #edf2f7; }
.has-file .file-label { border-style: solid; border-color: #2dce89; background-color: rgba(45, 206, 137, 0.1); }
.has-error .file-label { border-style: solid; border-color: #f5365c; background-color: rgba(245, 54, 92, 0.08); }
.has-error .file-name { color: #f5365c; }
.file-name { font-weight: 600; color: #2dce89; }
.download-template { color: #5e72e4; font-size: 0.875rem; text-decoration: none; }
.download-template:hover { color: #324cdd; text-decoration: underline; }
.elegant-table { border: 1px solid #e9ecef; border-radius: 6px; overflow: hidden; }
.elegant-table thead th { background-color: #f6f9fc; border-bottom: 1px solid #e9ecef; font-weight: 600; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.5px; }
.elegant-table tbody tr { transition: background-color 0.15s ease; }
.elegant-table tbody tr:hover { background-color: #f8f9fa; }
.elegant-footer { border-top: 1px solid #e9ecef; border-radius: 0 0 10px 10px; padding: 1rem 1.5rem; }
</style>